import { useEffect, useState } from "react";
import { api, formatError, fmtDuration } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading } from "@/components/Layout";
import { Modal } from "@/pages/Channels";
import { Plus, Stack, Trash, Clock, PencilSimple, ListPlus, CurrencyDollar, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";

const BLOCK_TYPES = ["general", "documentary", "sports", "news", "music", "entertainment", "promo"];

export const EVENT_TYPE_META = {
  content: { label: "Content", cls: "text-[#3A86FF] bg-[#3A86FF]/10 border-[#3A86FF]/20", dot: "bg-[#3A86FF]" },
  promo: { label: "Promo", cls: "text-violet-400 bg-violet-500/10 border-violet-500/20", dot: "bg-violet-400" },
  station_id: { label: "Station ID", cls: "text-amber-500 bg-amber-500/10 border-amber-500/20", dot: "bg-amber-500" },
  ad_break: { label: "Ad Break", cls: "text-emerald-500 bg-emerald-500/10 border-emerald-500/20", dot: "bg-emerald-500" },
  bumper: { label: "Bumper", cls: "text-pink-400 bg-pink-500/10 border-pink-500/20", dot: "bg-pink-400" },
};
const EVENT_TYPES = Object.keys(EVENT_TYPE_META);

export default function Programming() {
  const [blocks, setBlocks] = useState(null);
  const [editing, setEditing] = useState(null); // block or {} for new
  const { user } = useAuth();
  const canEdit = ["owner", "admin", "operator"].includes(user?.role);

  const load = () => api.get("/blocks").then((r) => setBlocks(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this programming block?")) return;
    await api.delete(`/blocks/${id}`);
    toast.success("Block deleted");
    load();
  };

  return (
    <>
      <PageHeader title="Programming" subtitle="Reusable programming blocks · the core scheduling unit"
        actions={canEdit && (
          <button data-testid="new-block-btn" onClick={() => setEditing({ name: "", description: "", block_type: "general", events: [] })}
            className="flex items-center gap-2 bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 text-white text-sm font-medium px-4 py-2 rounded-sm">
            <Plus size={16} weight="bold" /> New Block
          </button>
        )} />

      <div className="p-8 ccs-enter">
        {!blocks ? <Loading /> : blocks.length === 0 ? (
          <div className="text-center py-24 text-[#737373]">
            <Stack size={40} className="mx-auto mb-3 opacity-50" />
            <p className="text-sm">No programming blocks. Build one by grouping media assets.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {blocks.map((b) => (
              <div key={b.id} data-testid={`block-card-${b.id}`}
                className="bg-[#141414] border border-[#262626] rounded-sm p-5 hover:border-[#3a3a3a] transition-colors duration-150">
                <div className="flex items-start justify-between mb-3">
                  <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm bg-violet-500/10 text-violet-400 border border-violet-500/20">{b.block_type}</span>
                  {canEdit && (
                    <div className="flex gap-1">
                      <button data-testid={`edit-block-${b.id}`} onClick={() => setEditing(b)} className="text-[#737373] hover:text-white p-1 transition-colors duration-150"><PencilSimple size={15} /></button>
                      <button data-testid={`delete-block-${b.id}`} onClick={(e) => remove(b.id, e)} className="text-[#737373] hover:text-red-500 p-1 transition-colors duration-150"><Trash size={15} /></button>
                    </div>
                  )}
                </div>
                <h3 className="font-head font-semibold text-base">{b.name}</h3>
                <p className="text-xs text-[#737373] mt-1 line-clamp-2 h-8">{b.description || "No description"}</p>
                <div className="flex flex-wrap gap-1 mt-3">
                  {Object.entries((b.events || []).reduce((acc, e) => {
                    const t = e.event_type || "content"; acc[t] = (acc[t] || 0) + 1; return acc;
                  }, {})).map(([t, n]) => (
                    <span key={t} className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${EVENT_TYPE_META[t]?.cls || EVENT_TYPE_META.content.cls}`}>
                      {EVENT_TYPE_META[t]?.label || t} {n}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#262626] font-mono text-[10px] text-[#525252]">
                  <span className="flex items-center gap-1"><ListPlus size={12} /> {b.events.length} EVENTS</span>
                  <span className="flex items-center gap-1"><Clock size={12} /> {fmtDuration(b.total_duration)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && <BlockEditor block={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </>
  );
}

function BlockEditor({ block, onClose, onSaved }) {
  const [form, setForm] = useState({ name: block.name, description: block.description || "", block_type: block.block_type || "general" });
  const [events, setEvents] = useState(block.events || []);
  const [media, setMedia] = useState([]);
  const [saving, setSaving] = useState(false);
  const { org } = useAuth();
  const orgScte = !!org?.features?.scte;

  useEffect(() => { api.get("/assets").then((r) => setMedia(r.data)); }, []);

  const addAsset = (m) => setEvents([...events, {
    event_type: "content", asset_id: m.id, title: m.title, duration_seconds: m.duration_seconds,
    thumbnail: m.thumbnail, hls_url: m.hls_url,
  }]);
  const addAdBreak = () => setEvents([...events, {
    event_type: "ad_break", asset_id: null, title: "Ad Break", duration_seconds: 120,
    thumbnail: null, hls_url: "https://cdn.revine.tv/ads/slate/index.m3u8",
  }]);
  const setEventType = (i, t) => setEvents(events.map((e, idx) => idx === i ? { ...e, event_type: t } : e));
  const setEventDuration = (i, v) => setEvents(events.map((e, idx) => idx === i ? { ...e, duration_seconds: Math.max(0, parseInt(v || 0, 10)) } : e));
  const setScte = (i, patch) => setEvents(events.map((e, idx) => idx === i
    ? { ...e, scte: { enabled: false, segmentation_type_id: 52, auto_return: true, ...(e.scte || {}), ...patch } } : e));
  const removeEvent = (i) => setEvents(events.filter((_, idx) => idx !== i));
  const moveEvent = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= events.length) return;
    const next = [...events];
    [next[i], next[j]] = [next[j], next[i]];
    setEvents(next);
  };
  const total = events.reduce((a, e) => a + (e.duration_seconds || 0), 0);

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, events };
      if (block.id) await api.put(`/blocks/${block.id}`, payload);
      else await api.post("/blocks", payload);
      toast.success("Block saved");
      onSaved();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#141414] border border-[#262626] rounded-sm w-full max-w-6xl xl:max-w-7xl max-h-[90vh] flex flex-col ccs-enter">
        <div className="px-5 py-3 border-b border-[#262626] flex items-center justify-between">
          <h3 className="font-head font-semibold text-sm">{block.id ? "Edit" : "New"} Programming Block</h3>
          <div className="font-mono text-xs text-[#737373]">{events.length} events · {fmtDuration(total)}</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 flex-1 overflow-hidden">
          <div className="p-5 border-r border-[#262626] overflow-y-auto space-y-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Block name</label>
              <input data-testid="block-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none transition-colors duration-150" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Type</label>
              <select data-testid="block-type-select" value={form.block_type} onChange={(e) => setForm({ ...form, block_type: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none capitalize">
                {BLOCK_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Description</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2}
                className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none transition-colors duration-150" />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] font-bold">Playlist ({events.length})</div>
                <button data-testid="add-adbreak-btn" onClick={addAdBreak}
                  className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-emerald-500 hover:text-emerald-400 transition-colors duration-150">
                  <CurrencyDollar size={13} /> Ad Break
                </button>
              </div>
              <div className="space-y-2">
                {events.length === 0 && <div className="text-xs text-[#525252] py-4 text-center border border-dashed border-[#262626] rounded-sm">Add assets from the catalog →</div>}
                {events.map((e, i) => {
                  const etype = e.event_type || "content";
                  const meta = EVENT_TYPE_META[etype] || EVENT_TYPE_META.content;
                  return (
                    <div key={i} data-testid={`block-event-${i}`} className="flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] rounded-sm p-2">
                      <span className="font-mono text-[10px] text-[#525252] w-4">{i + 1}</span>
                      {e.thumbnail
                        ? <img src={e.thumbnail} alt="" className="h-8 w-12 object-cover rounded-sm" />
                        : <div className={`h-8 w-12 rounded-sm flex items-center justify-center border ${meta.cls}`}><CurrencyDollar size={14} /></div>}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs truncate">{e.title}</div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          <select data-testid={`event-type-${i}`} value={etype} onChange={(ev) => setEventType(i, ev.target.value)}
                            className={`text-[9px] font-mono uppercase tracking-wider rounded-sm border px-1 py-0.5 outline-none bg-[#0A0A0A] ${meta.cls}`}>
                            {EVENT_TYPES.map((t) => <option key={t} value={t} className="bg-[#141414] text-white">{EVENT_TYPE_META[t].label}</option>)}
                          </select>
                          {etype === "ad_break" ? (
                            <input data-testid={`event-duration-${i}`} type="number" min="0" value={e.duration_seconds}
                              onChange={(ev) => setEventDuration(i, ev.target.value)}
                              className="w-16 bg-[#0A0A0A] border border-[#262626] rounded-sm px-1 py-0.5 font-mono text-[10px] text-[#A3A3A3] outline-none focus:border-[#3A86FF]" />
                          ) : (
                            <span className="font-mono text-[10px] text-[#525252]">{fmtDuration(e.duration_seconds)}</span>
                          )}
                          {etype === "ad_break" && orgScte && (
                            <>
                              <button type="button" data-testid={`event-scte-toggle-${i}`} onClick={() => setScte(i, { enabled: !(e.scte?.enabled) })}
                                className={`text-[9px] font-mono uppercase tracking-wider rounded-sm border px-1.5 py-0.5 transition-colors duration-150 ${e.scte?.enabled ? "text-emerald-500 border-emerald-500/40 bg-emerald-500/10" : "text-[#737373] border-[#262626] hover:border-emerald-500/40"}`}>
                                SCTE-35 {e.scte?.enabled ? "ON" : "OFF"}
                              </button>
                              {e.scte?.enabled && (
                                <>
                                  <select data-testid={`event-scte-type-${i}`} value={e.scte?.segmentation_type_id ?? 52}
                                    onChange={(ev) => setScte(i, { segmentation_type_id: parseInt(ev.target.value, 10) })}
                                    className="text-[9px] font-mono rounded-sm border border-[#262626] bg-[#0A0A0A] px-1 py-0.5 outline-none text-[#A3A3A3]">
                                    <option value={52} className="bg-[#141414]">Provider Ad</option>
                                    <option value={54} className="bg-[#141414]">Distributor Ad</option>
                                  </select>
                                  <label className="flex items-center gap-1 text-[9px] font-mono text-[#737373]">
                                    <input type="checkbox" data-testid={`event-scte-autoreturn-${i}`} checked={e.scte?.auto_return !== false}
                                      onChange={(ev) => setScte(i, { auto_return: ev.target.checked })} /> auto-return
                                  </label>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col">
                        <button data-testid={`event-move-up-${i}`} onClick={() => moveEvent(i, -1)} disabled={i === 0}
                          className="text-[#737373] hover:text-white transition-colors duration-150 disabled:opacity-30 disabled:hover:text-[#737373]"><ArrowUp size={12} /></button>
                        <button data-testid={`event-move-down-${i}`} onClick={() => moveEvent(i, 1)} disabled={i === events.length - 1}
                          className="text-[#737373] hover:text-white transition-colors duration-150 disabled:opacity-30 disabled:hover:text-[#737373]"><ArrowDown size={12} /></button>
                      </div>
                      <button data-testid={`remove-event-${i}`} onClick={() => removeEvent(i)} className="text-[#737373] hover:text-red-500 p-1 transition-colors duration-150"><Trash size={14} /></button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="p-5 overflow-y-auto">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-2 font-bold">Media Catalog</div>
            <div className="space-y-2">
              {media.map((m) => (
                <button key={m.id} data-testid={`add-asset-${m.id}`} onClick={() => addAsset(m)}
                  className="w-full flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] hover:border-[#3A86FF] rounded-sm p-2 text-left transition-colors duration-150 group">
                  <img src={m.thumbnail} alt="" className="h-9 w-14 object-cover rounded-sm" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs truncate">{m.title}</div>
                    <div className="font-mono text-[10px] text-[#525252]">{m.category} · {fmtDuration(m.duration_seconds)}</div>
                  </div>
                  <Plus size={16} className="text-[#525252] group-hover:text-[#3A86FF] transition-colors duration-150" />
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-[#262626] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#A3A3A3] hover:text-white transition-colors duration-150">Cancel</button>
          <button data-testid="save-block-btn" onClick={save} disabled={saving}
            className="px-4 py-2 text-sm bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 rounded-sm font-medium disabled:opacity-60">
            {saving ? "Saving…" : "Save Block"}
          </button>
        </div>
      </div>
    </div>
  );
}
